import { useEffect, useState, useCallback } from 'react';
import { companyAPI } from '../../services/api';

/**
 * Latest configured exchange rate for a currency pair, expressed as
 * "units of `to` per 1 unit of `from`".
 *
 * Customer/vendor balances are stored in the company base currency, so screens
 * that want to show them in the party's own currency need the current rate
 * rather than the rate stored on a document.
 */
export function rateFor(rates, from, to) {
  if (!from || !to || from === to) return 1;
  const direct = rates.find(r => r.from_currency === from && r.to_currency === to);
  if (direct && parseFloat(direct.rate) > 0) return parseFloat(direct.rate);
  const reverse = rates.find(r => r.from_currency === to && r.to_currency === from);
  if (reverse && parseFloat(reverse.rate) > 0) return 1 / parseFloat(reverse.rate);
  return 1;
}

let cache = null;
let inflight = null;

export default function useExchangeRates() {
  const [rates, setRates] = useState(cache || []);

  useEffect(() => {
    if (cache) return undefined;
    if (!inflight) {
      inflight = companyAPI.exchangeRates()
        .then((rows) => { cache = Array.isArray(rows) ? rows : []; return cache; })
        .catch(() => [])
        .finally(() => { inflight = null; });
    }
    let mounted = true;
    inflight.then((rows) => { if (mounted) setRates(rows || []); });
    return () => { mounted = false; };
  }, []);

  const getRate = useCallback((from, to) => rateFor(rates, from, to), [rates]);
  return { rates, getRate };
}
